import React, { ComponentProps } from "react";
import styled from "styled-components";
import {
  Text,
  Flex,
  PopupMenu,
  buildPath,
  Icon,
  Box,
  Space,
  Kit,
  KitCollection,
  Button,
  KitCollectionItem,
  Callback,
  useNavigation,
} from "@thenounproject/lingo-core";

import KitCollectionMenuItems from "./KitCollectionMenuItems";

import KitList from "./KitList";

const Overflow = styled.button`
  padding: 0;
  line-height: 0;
`;

const KitListWrapper = styled(Box).attrs({
  width: "100%",
  p: "xs",
})`
  aspect-ratio: 16/10;
`;

const Container = styled(Flex).attrs({
  width: "100%",
  justifyContent: "space-between",
})`
  @media screen and (max-width: 500px) {
    flex-direction: column;
  }
`;
const DetailsContainer = styled(Flex).attrs(() => {
  return {
    mt: "s",
    p: "xs",
    justifyContent: "space-between",
    alignItems: "center",
    minHeight: "80px",
  };
})`
  width: 100%;
  @media screen and (min-width: 500px) {
    width: 50%;
    flex-direction: column;
    align-items: flex-start;
  }
  @media screen and (min-width: 768px) {
    width: 38%;
  }
`;

const KitsContainer = styled(Flex)`
  width: 50%;
  @media screen and (max-width: 500px) {
    width: 100%;
  }
`;

const EmptyCollectionContainer = styled(Flex).attrs({
  width: "100%",
  textAlign: "center",
  alignItems: "center",
  flexDirection: "column",
  borderRadius: "default",
  background: "grayLightest",
  justifyContent: "center",
  px: "s",
})`
  aspect-ratio: 16/10;
`;

type Props = {
  collection: Omit<KitCollection, "items"> & { items: KitCollectionItem[] };
  popupProps: ComponentProps<typeof PopupMenu>;
  setMenuOpen: Callback;
  menuOpen: boolean;
  navigate: ReturnType<typeof useNavigation>;
  space?: Space;
};

const KitCollectionCardDockweiler: React.FC<Props> = ({
  collection,
  space,
  popupProps,
  menuOpen,
  setMenuOpen,
  navigate,
}) => {
  const kits = collection.items.slice(0, 4).map(item => item.kit as Kit);

  function renderKitsElements() {
    if (kits.length === 0) {
      return (
        <EmptyCollectionContainer>
          <Box width="100%" maxWidth="400px" m="auto">
            <Text font="ui.regularBold" color="grayDarker" mb="xs">
              Add Kits
            </Text>
            <Text color="grayDarker">Open this collection to add Kits.</Text>
          </Box>
        </EmptyCollectionContainer>
      );
    }

    return (
      <KitListWrapper>
        <KitList
          kits={kits}
          kitCardSize="compact"
          gap={kits.length === 1 ? "0" : "16px"}
          columns={kits.length === 1 ? 1 : 2}
          maxRows={kits.length === 1 ? 1 : 2}
          identifier={collection.uuid}
        />
      </KitListWrapper>
    );
  }

  return (
    <Container>
      <DetailsContainer>
        <Flex flexDirection="column" width="100%">
          <Text font="ui.mediumSubtitle" mb="s">
            {collection.name}
          </Text>
          <Flex mb="l">
            <Text font="ui.title" color="grayDarkest" mr="s">
              {collection.counts.kits || 0} Kits
            </Text>
            <Overflow
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                setMenuOpen();
              }}
              data-popup-source={collection.uuid}>
              <Icon iconId="navigation.overflow" size="24" />
            </Overflow>
          </Flex>
          {menuOpen && (
            <PopupMenu {...popupProps}>
              <KitCollectionMenuItems kitCollectionId={collection.uuid} />
            </PopupMenu>
          )}
          <Box>
            <Button
              buttonStyle="tertiary"
              text="View collection"
              icon="navigation.chevron-right"
              onClick={() => {
                navigate.push(buildPath(collection.url, { space }));
              }}
            />
          </Box>
        </Flex>
      </DetailsContainer>
      <KitsContainer>{renderKitsElements()}</KitsContainer>
    </Container>
  );
};

export default KitCollectionCardDockweiler;
